import type { IChartDataSignal } from "../../scheme/components/analytics/interfaces";

import { MULTI_ANALYSIS_MEASURE_TYPE, type TMultiAnalysisSyncableMeasureType } from "../types";
import { convertClickedSpectrumXToHz } from "./frequency-profile";

const UNITS_ORDERS = 4;
const UNITS_REVOLUTIONS = 6;

const isPositiveRpm = (rotationFrequencyRpm: number | null | undefined): rotationFrequencyRpm is number =>
  rotationFrequencyRpm != null && Number.isFinite(rotationFrequencyRpm) && rotationFrequencyRpm > 0;

export const convertNativeXToPhysicalX = (
  nativeX: number,
  measureType: TMultiAnalysisSyncableMeasureType,
  unitsXCode: number | null | undefined,
  rotationFrequencyRpm: number | null | undefined,
): number | null => {
  if (measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM) {
    return convertClickedSpectrumXToHz(nativeX, unitsXCode, rotationFrequencyRpm);
  }

  if (!Number.isFinite(nativeX)) {
    return null;
  }

  if (unitsXCode === UNITS_REVOLUTIONS) {
    if (!isPositiveRpm(rotationFrequencyRpm)) {
      return null;
    }

    return (nativeX * 60_000) / rotationFrequencyRpm;
  }

  return nativeX;
};

export const convertPhysicalXToNativeX = (
  physicalX: number,
  measureType: TMultiAnalysisSyncableMeasureType,
  unitsXCode: number | null | undefined,
  rotationFrequencyRpm: number | null | undefined,
): number | null => {
  if (!Number.isFinite(physicalX)) {
    return null;
  }

  if (measureType === MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM) {
    if (unitsXCode === UNITS_ORDERS) {
      if (!isPositiveRpm(rotationFrequencyRpm)) {
        return null;
      }

      return physicalX / (rotationFrequencyRpm / 60);
    }

    return physicalX;
  }

  if (unitsXCode === UNITS_REVOLUTIONS) {
    if (!isPositiveRpm(rotationFrequencyRpm)) {
      return null;
    }

    return (physicalX * rotationFrequencyRpm) / 60_000;
  }

  return physicalX;
};

export const resolveSyncNativeValues = (
  physicalX: number | null | undefined,
  measureType: TMultiAnalysisSyncableMeasureType,
  seriesList: readonly IChartDataSignal[],
): readonly (number | null)[] | null => {
  if (physicalX == null || !Number.isFinite(physicalX)) {
    return null;
  }

  return seriesList.map(series =>
    convertPhysicalXToNativeX(physicalX, measureType, series.unitsXCode, series.rotationFrequencyRpm),
  );
};
